import React, { useState, useRef, useEffect } from "react";
import {
  Add,
  ChevronLeft,
  ChevronRight,
  Delete,
  ImageNotSupported,
} from "@mui/icons-material";
import {
  addMainBanner,
  getMainBanner,
  deleteMainBanner,
} from "../../services/dashboardService.js";

import ImageCropper from "../../components/ImageCropperModal/ImageCropper.jsx";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteModal.jsx";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const MainCarousel = ({
  initialBanners = [],
  editable = true,
  autoScroll = true,
  scrollInterval = 3000,
  aspectRatio = 16 / 5,
}) => {
  const bannerFileInputRef = useRef(null);
  const getImageUrl = (banner) => banner.details?.url || banner.url;
  const getBannerHref = (banner) => banner.href || banner.details?.href || "#";

  const [bannerData, setBannerData] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [showBannerForm, setShowBannerForm] = useState(false);
  const [bannerFormData, setBannerFormData] = useState({
    image: null,
    imageUrl: null,
    href: "",
  });
  const [loading, setLoading] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [isAutoScrolling, setIsAutoScrolling] = useState(autoScroll);
  const [isApiMode, setIsApiMode] = useState(true);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBannerId, setSelectedBannerId] = useState(null);

  useEffect(() => {
    if (initialBanners.length > 0) {
      setIsApiMode(false);
      setBannerData(initialBanners);
    } else {
      setIsApiMode(true);
      handleFetchBanners();
    }
  }, []);

  useEffect(() => {
    if (bannerData.length > 1 && isAutoScrolling) {
      const intervalId = setInterval(() => {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === bannerData.length - 1 ? 0 : prevIndex + 1
        );
      }, scrollInterval);

      return () => clearInterval(intervalId);
    }
  }, [bannerData.length, isAutoScrolling, scrollInterval]);

  const handleFetchBanners = async () => {
    try {
      setLoading(true);
      const response = await getMainBanner();
      if (response.success && response.data) {
        setBannerData(response.data);
      }
    } catch (error) {
      toast.error("Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  const handlePrevImage = () => {
    setIsAutoScrolling(false);
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? bannerData.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setIsAutoScrolling(false);
    setCurrentImageIndex((prevIndex) =>
      prevIndex === bannerData.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleDeleteClick = (bannerId) => {
    setSelectedBannerId(bannerId);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBannerId) return;

    try {
      setLoading(true);
      const updatedBannerData = bannerData.filter(
        (banner) => banner.id !== selectedBannerId
      );

      if (isApiMode) {
        const response = await deleteMainBanner(selectedBannerId);
        if (response.success) {
          toast.success("Banner deleted successfully");
        } else {
          toast.error(response.message || "Failed to delete banner");
          return;
        }
      } else {
        toast.success("Banner deleted successfully");
      }

      setBannerData(updatedBannerData);
      if (currentImageIndex >= updatedBannerData.length) {
        setCurrentImageIndex(Math.max(0, updatedBannerData.length - 1));
      }
    } catch (error) {
      toast.error("Failed to delete banner");
    } finally {
      setLoading(false);
      setIsDeleteModalOpen(false);
      setSelectedBannerId(null);
    }
  };

  const handleBannerFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImage(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error("Please upload a valid image file.");
    }
    event.target.value = "";
  };

  const handleCropComplete = (croppedBlob) => {
    const croppedUrl = URL.createObjectURL(croppedBlob);
    setBannerFormData((prev) => ({
      ...prev,
      image: croppedBlob,
      imageUrl: croppedUrl,
    }));
    setShowCropper(false);
  };

  const validateBannerForm = () => {
    if (!bannerFormData.image) {
      toast.error("Please select an image");
      return false;
    }
    if (!bannerFormData.href) {
      toast.error("Please enter a link URL");
      return false;
    }
    return true;
  };

  const handleBannerFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateBannerForm()) return;

    try {
      setSavingBanner(true);

      if (isApiMode) {
        const file = new File([bannerFormData.image], "banner.jpg", {
          type: bannerFormData.image.type || "image/jpeg",
        });

        const formData = new FormData();
        formData.append("banner", file);
        formData.append("href", bannerFormData.href);

        const result = await addMainBanner({ payload: formData });

        if (result.success) {
          await handleFetchBanners();
          toast.success(result.message || "Banner added successfully");
        } else {
          toast.error(result.message || "Failed to add banner");
        }
      } else {
        const newBanner = {
          id: Date.now().toString(),
          details: { url: bannerFormData.imageUrl },
          url: bannerFormData.imageUrl,
          href: bannerFormData.href,
        };
        const updatedData = [...bannerData, newBanner];
        setBannerData(updatedData);
        toast.success("Banner added successfully");
      }

      setBannerFormData({ image: null, imageUrl: null, href: "" });
      setShowBannerForm(false);
    } catch (error) {
      toast.error(error.message || "Failed to add banner");
    } finally {
      setSavingBanner(false);
    }
  };

  const handleBannerFormCancel = () => {
    setBannerFormData({ image: null, imageUrl: null, href: "" });
    setShowBannerForm(false);
  };

  const handleMouseEnter = () => setIsAutoScrolling(false);
  const handleMouseLeave = () => autoScroll && setIsAutoScrolling(true);

  return (
    <div className="carousel-wrapper">
      <div className="carousel-container">
        {editable && (
          <button
            className="add-button"
            onClick={() => setShowBannerForm(true)}
          >
            <Add />
          </button>
        )}

        <div
          className="carousel-content"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            className="carousel-nav-btn prev-btn"
            onClick={handlePrevImage}
          >
            <ChevronLeft />
          </button>

          <div className="carousel-slider-container">
            {bannerData.length > 0 ? (
              <div
                className="carousel-slider"
                style={{
                  transform: `translateX(-${currentImageIndex * 100}%)`,
                }}
              >
                {bannerData.map((banner, index) => (
                  <div key={index} className="carousel-slide">
                    <Link
                      to={getBannerHref(banner)}
                    >
                      <img
                        src={getImageUrl(banner)}
                        alt={`Banner ${index + 1}`}
                        className="carousel-image"
                        loading="lazy"
                      />
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-image-message">
                <ImageNotSupported className="no-image-icon" />
                <p>No images available</p>
                {editable && <p>Click the + button to add a banner</p>}
              </div>
            )}
          </div>

          <button
            className="carousel-nav-btn next-btn"
            onClick={handleNextImage}
          >
            <ChevronRight />
          </button>
        </div>

        {editable && bannerData[currentImageIndex] && (
          <button
            className="delete-banner-btn"
            onClick={() => handleDeleteClick(bannerData[currentImageIndex].id)}
          >
            <Delete />
          </button>
        )}

        <div className="carousel-indicators">
          {bannerData.map((_, index) => (
            <span
              key={index}
              className={`indicator ${
                index === currentImageIndex ? "active" : ""
              }`}
              onClick={() => setCurrentImageIndex(index)}
            />
          ))}
        </div>

        {/* Banner Form Modal */}
        {showBannerForm && (
          <div className="banner-form-modal-overlay">
            <div className="banner-form-modal">
              <div className="header">
                <h5 className="form-title">Add New Banner</h5>  
              </div>
              
              <form onSubmit={handleBannerFormSubmit} className="banner-form">
                <div className="form-image-container">
                  {bannerFormData.imageUrl ? (
                    <img
                      src={bannerFormData.imageUrl}
                      alt="Banner preview"
                      className="form-image-preview"
                      loading="lazy"
                    />
                  ) : (
                    <div className="empty-image-container">
                      <div className="empty-image-placeholder">
                        <ImageNotSupported className="empty-image-icon" />
                        <p>No image selected</p>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    className="form-image-upload-btn"
                    onClick={() => bannerFileInputRef.current?.click()}
                    disabled={savingBanner}
                  >
                    Upload
                  </button>
                </div>

                <div className="form-field">
                  <label htmlFor="banner-href">Link URL</label>
                  <input
                    id="banner-href"
                    type="text"
                    value={bannerFormData.href}
                    onChange={(e) =>
                      setBannerFormData({
                        ...bannerFormData,
                        href: e.target.value,
                      })
                    }
                    placeholder="Enter banner link URL"
                    disabled={savingBanner}
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="cancel-btn"
                    onClick={handleBannerFormCancel}
                    disabled={savingBanner}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`save-btn ${savingBanner ? "saving" : ""}`}
                    disabled={!bannerFormData.image || savingBanner}
                  >
                    {savingBanner ? "Saving..." : "Save Banner"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showCropper && (
          <div className="cropper-modal-overlay">
            <div className="cropper-modal-content">
              <ImageCropper
                open={showCropper}
                onClose={() => setShowCropper(false)}
                imageSrc={cropImage}
                onCropComplete={handleCropComplete}
                aspectRatio={aspectRatio}
                title="Banner Image Cropper"
              />
            </div>
          </div>
        )}

        <input
          type="file"
          ref={bannerFileInputRef}
          style={{ display: "none" }}
          accept="image/*"
          onChange={handleBannerFileChange}
        />
      </div>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default MainCarousel;



